// Reservation Policies translations for React Admin resource
export const reservationPoliciesResource = {
  name: 'Reservation Policy |||| Reservation Policies',
  fields: {
    id: 'ID',
    name: 'Policy Name',
    description: 'Description',
    isSystemDefault: 'System Default',
    reservationMode: 'Reservation Mode',
    accessMode: 'Access Mode',
    feePolicy: 'Fee Policy',
    slotDurationMinutes: 'Slot Duration (minutes)',
    slotGranularityMinutes: 'Slot Granularity (minutes)',
    holdWindowMinutes: 'Hold Window (minutes)',
    leadTimeMinutes: 'Lead Time (minutes)',
    cancelWindowMinutes: 'Cancel Window (minutes)',
    modifyWindowMinutes: 'Modify Window (minutes)',
    maxAdvanceDays: 'Max Advance Days',
    maxActivePerUser: 'Max Active Per User',
    maxModifications: 'Max Modifications',
    liveStatusHorizonHours: 'Live Status Horizon (hours)',
    blockedNoshowToleranceMinutes: 'Blocked No-Show Tolerance (minutes)',
    forfeitOnBlockedNoShow: 'Forfeit Hold When Blocked',
    blockedDetectionIncrementMinutes: 'Blocked Detection Increment (minutes per tick)',
    allowedMembershipLevels: 'Allowed Membership Levels',
    allowedGroupIds: 'Allowed User Groups',
    createdAt: 'Created',
    updatedAt: 'Updated',
    createdBy: 'Created By',
    updatedBy: 'Updated By',
  },
};

// Custom reservation-policies translations for components
export const reservationPolicies = {
  subtitle: 'Reusable reservation behaviour presets',
  list: {
    title: 'Reservation Policies',
    subtitle: 'All reservation policies',
    searchPlaceholder: 'Search policies',
    noPolicies: 'No reservation policies defined yet',
    filters: {
      search: 'Search',
    },
  },
  create: {
    title: 'Create Reservation Policy',
    subtitle: 'Define a new reservation policy preset',
    backToList: 'Back to policies',
  },
  edit: {
    title: 'Edit Reservation Policy',
    subtitle: 'Modify policy configuration',
    backToList: 'Back to policies',
  },
  show: {
    subtitle: 'Policy details',
  },
  sections: {
    identity: 'Identity',
    timing: 'Mode',
    timingWindows: 'Timing & Windows',
    feeBehavior: 'Fee Behavior',
    eligibility: 'Eligibility',
  },
  banners: {
    presetLibrary: 'Policies are reusable presets. Apply this policy to a charger by selecting it in the charger\'s Fee Template.',
    feeBehavior: 'The fee amount is configured per-charger in the Fee Template. This policy only defines the BEHAVIOR (release, apply to session, or forfeit on no-show).',
  },
  modes: {
    fixed_slot: 'Fixed Slot',
    next_slot: 'Next Slot',
  },
  accessModes: {
    reservation_only: 'Reservation Only',
    reserve_or_fcfs: 'Reserve or First-Come-First-Served',
  },
  feePolicies: {
    none: 'No Fee',
    hold_release: 'Hold & Release',
    hold_apply: 'Hold & Apply',
    hold_forfeit_on_noshow: 'Hold (forfeit on no-show)',
  },
  feePolicyTooltips: {
    none: 'No reservation fee charged.',
    hold_release: 'Hold fee from wallet at booking; always released back after session or on no-show.',
    hold_apply: 'Hold fee from wallet at booking; applied as prepayment to the charging session cost.',
    hold_forfeit_on_noshow: 'Hold fee from wallet at booking; released if user attends, captured if no-show.',
  },
  membershipLevels: {
    standard: 'Standard',
    silver: 'Silver',
    gold: 'Gold',
    platinum: 'Platinum',
    vip: 'VIP',
  },
  validation: {
    nameRequired: 'Policy name is required',
    durationTooShort: 'Slot duration must be at least 15 minutes',
    granularityInvalid: 'Granularity must divide slot duration',
    holdWindowTooShort: 'Hold window must be at least 5 minutes',
    leadTimeNegative: 'Lead time cannot be negative',
  },
  helpers: {
    name: 'Unique name for this policy preset',
    description: 'Optional description for internal reference',
    systemDefaultName: 'System default name cannot be changed',
    reservationMode: 'Fixed slot uses pre-defined windows; next-slot reserves the next available block',
    accessMode: 'Restrict access during reservation windows or allow walk-ins',
    accessModeFixedSlot: 'Fixed-slot policies are reservation-only: walk-ins are not allowed, so a non-reserved charge cannot overrun into a booked slot.',
    slotDuration: 'How long each reservation block lasts.',
    slotDurationDisabled: 'Not used in next_slot mode. The Hold Window value is used as the slot length instead.',
    naNextSlot: 'Not applicable in next_slot mode. Reservations start "now", so this window is meaningless.',
    slotGranularity: 'Increment for slot boundaries (must divide slot duration)',
    holdWindow: 'Time to keep a slot held after start time before marking no-show',
    leadTime: 'Minimum minutes ahead to book',
    cancelWindow: 'Cancellation deadline before slot start',
    modifyWindow: 'Modification deadline before slot start',
    maxAdvanceDays: 'How many days ahead a user can book',
    maxActivePerUser: 'Maximum concurrent active reservations per user',
    maxModifications: 'Maximum modifications allowed per reservation',
    liveStatusHorizonHours: 'Slots starting within this many hours respect the charger\'s current OCPP status (offline/faulted/maintenance/unavailable). Beyond this horizon, status is ignored, which protects week-out reservations from transient outages. Default 2.',
    feePolicy: 'How reservation fees are handled',
    blockedNoshowTolerance: 'If the user waited at least this many minutes while a prior occupant blocked the connector, the no-show is recorded but the wallet hold is released instead of forfeited. Default 5.',
    forfeitOnBlockedNoShow: 'Strict mode: forfeit the hold even when the user was blocked by a prior occupant. Default off.',
    blockedDetectionIncrement: 'How many minutes to add to the blocked counter each time the per-minute cron observes the connector still occupied. Default 1 (one tick = one minute). Set to 0 to disable the waiver counting for this policy.',
    allowedMembershipLevels: 'Restrict to specified membership levels (empty = all levels allowed)',
    allowedGroupIds: 'Restrict to users in specified groups (empty = no group restriction)',
  },
  notifications: {
    created: 'Reservation policy created successfully',
    updated: 'Reservation policy updated successfully',
    deleted: 'Reservation policy deleted successfully',
    error: {
      create: 'Failed to create policy',
      update: 'Failed to update policy',
      delete: 'Failed to delete policy',
    },
  },
  actions: {
    create: 'Create Policy',
    save: 'Save Changes',
    delete: 'Delete Policy',
  },
};

export const reservationPoliciesTranslations = {
  'reservation-policies': {
    ...reservationPoliciesResource,
    ...reservationPolicies,
  },
};
